import type { DefaultCmsDataResponse, Image, File } from '../types';

export interface Category {
  slug: string;
  count: number;
  description: string;
  link: null;
  createdAt: Date;
  updatedAt: Date;
  publishedAt: Date;
  locale: string;
  parent: number;
  name: string;
  id: number;
}

export interface PostTag {
  createdAt: Date;
  updatedAt: Date;
  publishedAt: Date;
  locale: string;
  name: string;
  slug: string;
  count: number;
  link: null;
  description: string;
}

export interface Post {
  title: string;
  content: string;
  author: number;
  slug: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  summary: string;
  thumbnail_url: string;
  link?: string;
  external_link?: string;
  file: {
    data: DefaultCmsDataResponse<File>;
  };
  images: {
    data: DefaultCmsDataResponse<Image>[];
  };
  categories: {
    data: DefaultCmsDataResponse<Category>[];
  };
  tags: {
    data: DefaultCmsDataResponse<PostTag>[];
  };
  follow?: boolean;
  index?: boolean;
  canonical_url?: string;
}

export enum DisplayCategory {
  KHACH_HANG = 'khach-hang',
  TAI_XE = 'tai-xe',
  AHAMOVERS = 'ahamovers',
  DOI_TAC = 'doi-tac',
  DONG_HANH_CUNG_THUONG_HIEU = 'dong-hanh-cung-thuong-hieu',
}

export enum DisplayCategoryBlog {
  THONG_TIN_DICH_VU = 'thong-tin-dich-vu',
  KIEN_THUC_GIAO_HANG = 'kien-thuc-giao-hang',
  // kinh doanh
  KINH_NGHIEM_MO_CUA_HANG = 'kinh-nghiem-mo-cua-hang',
  BAN_HANG_ONLINE = 'ban-hang-online',
  KINH_DOANH_TREN_FACEBOOK = 'kinh-doanh-tren-facebook',
  KINH_DOANH_TREN_SHOPEE = 'kinh-doanh-tren-shopee',
  KINH_DOANH_TREN_TIKTOK_SHOP = 'kinh-doanh-tren-tiktok-shop',
  KINH_DOANH_TREN_LAZADA = 'kinh-doanh-tren-lazada',
  KIEN_THUC_MAR_KETING = 'kien-thuc-marketing',
  Y_TUONG_KINH_DOANH = 'y-tuong-kinh-doanh',
  CHO_DAU_MOI = 'cho-dau-moi',
  CACH_NHAP_HANG = 'cach-nhap-hang',
  KHO_BAI = 'kho-bai',
}

export const displayCategories: Record<
  string,
  { id: number; slug: string; name: string }
> = {
  [DisplayCategory.KHACH_HANG]: {
    id: 2,
    slug: DisplayCategory.KHACH_HANG,
    name: 'Khách hàng',
  },
  [DisplayCategory.TAI_XE]: {
    id: 5,
    slug: DisplayCategory.TAI_XE,
    name: 'Tài xế',
  },
  [DisplayCategory.AHAMOVERS]: {
    id: 152,
    slug: DisplayCategory.AHAMOVERS,
    name: 'Ahamovers',
  },
  [DisplayCategory.DOI_TAC]: {
    id: 3,
    slug: DisplayCategory.DOI_TAC,
    name: 'Đối tác',
  },
  [DisplayCategory.DONG_HANH_CUNG_THUONG_HIEU]: {
    id: 8,
    slug: DisplayCategory.DONG_HANH_CUNG_THUONG_HIEU,
    name: 'Đồng hành cùng Thương hiệu',
  },
};

export const displayCategoriesBlog: Record<
  string,
  { id: number; slug: string; name: string; group?: string }
> = {
  [DisplayCategoryBlog.THONG_TIN_DICH_VU]: {
    id: 9,
    slug: DisplayCategoryBlog.THONG_TIN_DICH_VU,
    name: 'Thông tin dịch vụ',
  },
  [DisplayCategoryBlog.KIEN_THUC_GIAO_HANG]: {
    id: 10,
    slug: DisplayCategoryBlog.KIEN_THUC_GIAO_HANG,
    name: 'Kiến thức giao hàng',
  },
};

export enum DisplayCategoryBlogKinhDoanh {
  THONG_TIN_DICH_VU = 'thong-tin-dich-vu',
  KIEN_THUC_GIAO_HANG = 'kien-thuc-giao-hang',
  KINH_NGHIEM_KINH_DOANH = 'kinh-nghiem-kinh-doanh',
  NGUON_HANG = 'nguon-hang',
  // kinh doanh
  KINH_NGHIEM_MO_CUA_HANG = 'kinh-nghiem-mo-cua-hang',
  BAN_HANG_ONLINE = 'ban-hang-online',
  KINH_DOANH_TREN_FACEBOOK = 'kinh-doanh-tren-facebook',
  KINH_DOANH_TREN_SHOPEE = 'kinh-doanh-tren-shopee',
  KINH_DOANH_TREN_TIKTOK_SHOP = 'kinh-doanh-tren-tiktok-shop',
  KINH_DOANH_TREN_LAZADA = 'kinh-doanh-tren-lazada',
  KIEN_THUC_MAR_KETING = 'kien-thuc-marketing',
  Y_TUONG_KINH_DOANH = 'y-tuong-kinh-doanh',
  CHO_DAU_MOI = 'cho-dau-moi',
  CACH_NHAP_HANG = 'cach-nhap-hang',
  KHO_BAI = 'kho-bai',
}

export const displayCategoriesBlogKinhDoanh: Record<
  string,
  {
    id: number;
    slug: string;
    name: string;
    group?: string;
    title?: string;
    desc?: string;
    metaDescription?: string;
  }
> = {
  [DisplayCategoryBlogKinhDoanh.KINH_NGHIEM_KINH_DOANH]: {
    id: 11,
    slug: DisplayCategoryBlogKinhDoanh.KINH_NGHIEM_KINH_DOANH,
    name: 'Kinh nghiệm kinh doanh',
    title: 'Tổng hợp kinh nghiệm kinh doanh cho người mới bắt đầu',
    metaDescription:
      'Tổng hợp kinh nghiệm thực chiến từ việc mở cửa hàng, bán hàng online đa kênh trên Shopee, TikTok, Facebook đến các chiến lược marketing hiệu quả.',
    desc: 'Chia sẻ kinh nghiệm kinh doanh từ online đến offline hiệu quả từ Kinhdoanh.ahamove',
  },
  [DisplayCategoryBlogKinhDoanh.NGUON_HANG]: {
    id: 12,
    slug: DisplayCategoryBlogKinhDoanh.NGUON_HANG,
    name: 'Nguồn hàng',
    title: 'Hướng dẫn tìm nguồn hàng hiệu quả cùng Kinhdoanh.ahamove',
    metaDescription:
      'Khám phá cách chọn nguồn hàng giá tốt, chất lượng từ chợ đầu mối. Hướng dẫn chi tiết từ Kinhdoanh.ahamove giúp tối ưu kinh doanh.',
    desc: 'Kinhdoanh.ahamove chia sẻ kinh nghiệm tìm nguồn hàng chất lượng trong và ngoài nước.',
  },

  // kinh doanh
  [DisplayCategoryBlogKinhDoanh.KINH_NGHIEM_MO_CUA_HANG]: {
    id: 17,
    slug: DisplayCategoryBlogKinhDoanh.KINH_NGHIEM_MO_CUA_HANG,
    name: 'Kinh nghiệm mở cửa hàng',
    group: DisplayCategoryBlogKinhDoanh.KINH_NGHIEM_KINH_DOANH,
    title: 'Tổng hợp kinh nghiệm mở cửa hàng dành cho người mới kinh doanh',
    metaDescription:
      'Tổng hợp kinh nghiệm mở cửa hàng từ chọn địa điểm, quản lý vốn đến tối ưu quy trình, giúp kinh doanh hiệu quả.',
    desc: 'Kinhdoanh.ahamove hướng dẫn từng bước mở cửa hàng kinh doanh chi tiết cho người mới bắt đầu',
  },
  [DisplayCategoryBlogKinhDoanh.BAN_HANG_ONLINE]: {
    id: 18,
    slug: DisplayCategoryBlogKinhDoanh.BAN_HANG_ONLINE,
    name: 'Bán hàng online',
    group: DisplayCategoryBlogKinhDoanh.KINH_NGHIEM_KINH_DOANH,
    title:
      'Bí quyết thành công và thu hút khách hàng khi bán hàng online từ Kinhdoanh.ahamove',
    metaDescription:
      'Kinhdoanh.ahamove hướng dẫn cách bán hàng online hiệu quả, từ xây dựng thương hiệu đến bí quyết bán hàng online thành công cho người mới bắt đầu.',
    desc: 'Cung cấp kinh nghiệm bán hàng online hiệu quả với những mẹo thu hút khách hàng từ Kinhdoanh.ahamove.',
  },
  [DisplayCategoryBlogKinhDoanh.KINH_DOANH_TREN_FACEBOOK]: {
    id: 19,
    slug: DisplayCategoryBlogKinhDoanh.KINH_DOANH_TREN_FACEBOOK,
    name: 'Kinh doanh trên Facebook',
    group: DisplayCategoryBlogKinhDoanh.KINH_NGHIEM_KINH_DOANH,
    title: 'Bí quyết kinh doanh Facebook hiệu quả cùng Kinhdoanh.ahamove',
    metaDescription:
      'Bí quyết kinh doanh trên Facebook từ tạo shop, cách thu hút tương tác khách hàng từ Kinhdoanh.ahamove chia sẻ kinh nghiệm thực tế.',
    desc: 'Bí quyết bán hàng thành công trên Facebook từ các mẹo tối ưu đến cách thu hút khách hàng',
  },
  [DisplayCategoryBlogKinhDoanh.KINH_DOANH_TREN_SHOPEE]: {
    id: 20,
    slug: DisplayCategoryBlogKinhDoanh.KINH_DOANH_TREN_SHOPEE,
    name: 'Kinh doanh trên Shopee',
    group: DisplayCategoryBlogKinhDoanh.KINH_NGHIEM_KINH_DOANH,
    title:
      'Kinhdoanh.ahamove chia sẻ bí quyết tăng đơn hàng nhanh chóng khi kinh doanh trên Shopee ',
    metaDescription:
      'Hướng dẫn bán hàng Shopee từ tạo shop đến những cách tối ưu gian hàng hiệu quả cùng những kinh nghiệm thực tế của Kinhdoanh.ahamove.',
    desc: 'Mẹo kinh doanh trên Shopee hiệu quả, tối ưu doanh thu cho người mới bắt đầu',
  },
  [DisplayCategoryBlogKinhDoanh.KINH_DOANH_TREN_TIKTOK_SHOP]: {
    id: 21,
    slug: DisplayCategoryBlogKinhDoanh.KINH_DOANH_TREN_TIKTOK_SHOP,
    name: 'Kinh doanh trên TikTok Shop',
    group: DisplayCategoryBlogKinhDoanh.KINH_NGHIEM_KINH_DOANH,
    title:
      'Mẹo kinh doanh TikTok Shop hiệu quả nhanh chóng từ Kinhdoanh.ahamove',
    metaDescription:
      'Bí quyết bán hàng TikTok Shop: livestream, content viral, Affiliate. Kinhdoanh.ahamove hướng dẫn chi tiết từ A-Z cho người mới.',
    desc: 'Kinh nghiệm bán hàng trên TikTok Shop giúp tăng tương tác, thu hút khách hàng tiềm năng',
  },
  [DisplayCategoryBlogKinhDoanh.KINH_DOANH_TREN_LAZADA]: {
    id: 22,
    slug: DisplayCategoryBlogKinhDoanh.KINH_DOANH_TREN_LAZADA,
    name: 'Kinh doanh trên Lazada',
    group: DisplayCategoryBlogKinhDoanh.KINH_NGHIEM_KINH_DOANH,
    title: 'Kinh nghiệm kinh doanh Lazada thành công từ Ahamove',
    metaDescription:
      'Hướng dẫn kinh doanh Lazada: tạo tài khoản, tối ưu sản phẩm,.... cho người mới bắt đầu. Kinh nghiệm thực tế từ Kinhdoanh.ahamove.',
    desc: 'Kinh nghiệm bán hàng Lazada hiệu quả từ chạy quảng cáo đến những cách tối ưu gian hàng cho chủ shop',
  },
  [DisplayCategoryBlogKinhDoanh.KIEN_THUC_MAR_KETING]: {
    id: 23,
    slug: DisplayCategoryBlogKinhDoanh.KIEN_THUC_MAR_KETING,
    name: 'Kiến thức Marketing',
    group: DisplayCategoryBlogKinhDoanh.KINH_NGHIEM_KINH_DOANH,
    title:
      'Kinhdoanh.ahamove cung cấp kiến thức Marketing cơ bản, tối ưu hiệu quả kinh doanh của bạn.',
    metaDescription:
      'Kiến thức marketing thực tiễn, dễ áp dụng, giúp tối ưu chiến lược kinh doanh hiệu quả từ Kinhdoanh.ahamove.',
    desc: 'Cung cấp kiến thức marketing thực tiễn, dễ áp dụng cho chiến lược kinh doanh của bạn',
  },
  [DisplayCategoryBlogKinhDoanh.Y_TUONG_KINH_DOANH]: {
    id: 24,
    slug: DisplayCategoryBlogKinhDoanh.Y_TUONG_KINH_DOANH,
    name: 'Ý tưởng kinh doanh',
    title: 'Tổng hợp ý tưởng kinh doanh mới nhất từ Kinhdoanh.ahamove',
    desc: 'Khám phá ngay danh sách các ý tưởng tiềm năng, ít vốn, và phù hợp với các xu hướng thị trường mới nhất.',
    metaDescription:
      'Khám phá ý tưởng kinh doanh ít vốn, tiềm năng và phù hợp với xu hướng thị trường. Các cơ hội khởi nghiệp hấp dẫn cho người mới.',
  },
  [DisplayCategoryBlogKinhDoanh.CHO_DAU_MOI]: {
    id: 25,
    slug: DisplayCategoryBlogKinhDoanh.CHO_DAU_MOI,
    name: 'Chợ đầu mối',
    group: DisplayCategoryBlogKinhDoanh.NGUON_HANG,
    title:
      'Tìm nguồn hàng chất lượng cho kinh doanh từ chợ đầu mối cùng Kinhdoanh.ahamove',
    metaDescription:
      'Khám phá cách chọn nguồn hàng giá tốt, chất lượng từ chợ đầu mối. Hướng dẫn chi tiết giúp tối ưu kinh doanh hiệu quả.',
    desc: 'Bí quyết lựa chọn nguồn hàng từ chợ đầu mối giá rẻ, chất lượng và uy tín',
  },
  [DisplayCategoryBlogKinhDoanh.CACH_NHAP_HANG]: {
    id: 26,
    slug: DisplayCategoryBlogKinhDoanh.CACH_NHAP_HANG,
    name: 'Cách nhập hàng',
    group: DisplayCategoryBlogKinhDoanh.NGUON_HANG,
    title: 'Bí quyết nhập hàng giá tốt, chất lượng từ Kinhdoanh.ahamove',
    metaDescription:
      'Tìm hiểu cách nhập hàng hiệu quả, tiết kiệm chi phí, đảm bảo chất lượng. Bí quyết cách nhập hàng thành công cho mọi mô hình kinh doanh.',
    desc: 'Kinhdoanh.ahamove cung cấp mẹo nhập hàng giá rẻ, đảm bảo chất lượng cho kinh doanh.',
  },
  [DisplayCategoryBlogKinhDoanh.KHO_BAI]: {
    id: 27,
    slug: DisplayCategoryBlogKinhDoanh.KHO_BAI,
    name: 'Kho bãi',
    title:
      'Kinhdoanh.ahamove cung cấp kiến thức và vị trí kho hàng từ Shopee, Lazada và các kho hàng khác.',
    metaDescription:
      'Kinhdoanh.ahamove chia sẻ kinh nghiệm thực tế về kho bãi từ phân loại, vị trí và bí quyết tối ưu kho hàng để giao hàng nhanh hơn.',
    desc: 'Kiến thức tối ưu kho bãi giúp tăng hiệu quả kinh doanh từ Kinhdoanh.ahamove',
  },
};
